/**
 * The one-box carer-guidance editor (Ed 2026-09-29): text on the left, the
 * carer's screen on the right, redrawn as you type. Shared by the catalogue
 * item editor (librarian) and the plan editor's per-plan override (planner).
 * Markup + wiring only; saving stays with each caller.
 */
import { escapeHtml } from "../utils/html";
import { renderGuidanceHtml } from "./render-html";
import { guidanceWarnings, parseGuidanceText } from "./single-text";

const HELP =
  "<code># Title</code> a section · <code># Level 1 · Fully supported</code> a numbered level · " +
  "<code>## Label</code> a small heading · <code>1.</code> steps · <code>-</code> bullets · " +
  "<code>- **5 Independent** — text</code> a score line · <code>&gt;</code> the boxed note · " +
  "<code>**bold**</code>. Anything else shows as plain text.";

function warningsHtml(text: string): string {
  const w = guidanceWarnings(parseGuidanceText(text));
  return w.length
    ? w.map((m) => `<div class="gtx-warn">⚠ ${escapeHtml(m)}</div>`).join("")
    : `<div class="gtx-ok">✓ Looks complete</div>`;
}

/**
 * `id` names the textarea; the preview and warnings take `${id}-preview` /
 * `${id}-warnings`. Read-only mode shows the carer's screen alone.
 */
export function guidanceEditorHtml(id: string, text: string, editable: boolean, placeholder = ""): string {
  const phone = `<div class="gtx-phone"><div class="gtx-phone-cap">What the carer sees</div>` +
    `<div id="${id}-preview">${renderGuidanceHtml(parseGuidanceText(text))}</div></div>`;
  if (!editable) return phone;
  return `<div class="gtx-editor"><div class="gtx-editor-left">` +
    `<textarea id="${id}" class="gtx-source" spellcheck="true" placeholder="${escapeHtml(placeholder)}">${escapeHtml(text)}</textarea>` +
    `<div id="${id}-warnings">${warningsHtml(text)}</div>` +
    `<div class="gtx-help">${HELP}</div></div>${phone}</div>`;
}

/** Redraw the preview + warnings on every keystroke, keeping folds as the author left them. */
export function wireGuidanceEditor(root: ParentNode, id: string, onInput?: (text: string) => void): void {
  const ta = root.querySelector<HTMLTextAreaElement>(`#${id}`);
  const preview = root.querySelector<HTMLElement>(`#${id}-preview`);
  const warnings = root.querySelector<HTMLElement>(`#${id}-warnings`);
  if (!ta || !preview) return;
  ta.addEventListener("input", () => {
    const open = [...preview.querySelectorAll("details")].map((d) => d.open);
    const doc = parseGuidanceText(ta.value);
    const keep = open.length === doc.folds.length;
    preview.innerHTML = renderGuidanceHtml(doc, (i) => (keep ? open[i] : i === 0));
    if (warnings) warnings.innerHTML = warningsHtml(ta.value);
    onInput?.(ta.value);
  });
}
